import React, { useState, useEffect } from 'react';
import { Modal, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import { getSmsTemplatesList, getSmsTemplateDetail } from '../../../../api/sms';

import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconButton,
  ModalContentArea,
  ContentRow,
  SearchInput,
  ListSection,
  ListHeader,
  ListTitle,
  ListCount,
  ListBody,
  TemplateListItem,
  TemplateName,
  TemplatePreview,
  EmptyText,
  DetailSection,
  DetailHeader,
  DetailTitle,
  DetailBody,
  DetailContentText,
  DetailPlaceholder,
  ButtonRow,
  CancelButton,
  AddButton,
} from './SmsTemplateSelectModalStyled';

const SmsTemplateSelectModal = ({ open, onClose, onSelect }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const branchId = user?.branch?.branchId;

  const [searchValue, setSearchValue] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const { data: templateList = [] } = useQuery({
    queryKey: ['smsTemplates', branchId],
    queryFn: () => getSmsTemplatesList(branchId, user?.token),
    enabled: open && !!branchId && !!user?.token,
  });

  const { data: templateDetail } = useQuery({
    queryKey: ['smsTemplateDetail', branchId, selectedTemplateId],
    queryFn: () => getSmsTemplateDetail(branchId, selectedTemplateId, user?.token),
    enabled: open && !!branchId && !!user?.token && !!selectedTemplateId,
  });

  useEffect(() => {
    if (!open) {
      setSearchValue('');
      setSelectedTemplateId(null);
    }
  }, [open]);

  const keyword = searchValue.trim().toLowerCase();
  const filteredList = keyword
    ? templateList.filter(
        item =>
          item.templateName?.toLowerCase().includes(keyword) ||
          item.content?.toLowerCase().includes(keyword),
      )
    : templateList;

  const handleAdd = () => {
    if (!selectedTemplateId) {
      toast.error(t('템플릿을 선택해주세요'));
      return;
    }
    const content = templateDetail?.content;
    if (!content) {
      toast.error(t('템플릿 내용을 불러오는 중입니다'));
      return;
    }
    onSelect(content);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalHeaderTitle>{t('템플릿 불러오기')}</ModalHeaderTitle>
          <CloseIconButton onClick={onClose}>
            <CloseIcon style={{ fontSize: '24px' }} />
          </CloseIconButton>
        </ModalHeader>

        <ModalContentArea>
          <SearchInput
            placeholder={t('템플릿 명 또는 내용으로 검색')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon style={{ color: '#a6a6a6' }} />
                </InputAdornment>
              ),
            }}
          />

          <ContentRow>
            <ListSection>
              <ListHeader>
                <ListTitle>{t('템플릿 목록')}</ListTitle>
                <ListCount>
                  {filteredList.length}
                  {t('개')}
                </ListCount>
              </ListHeader>
              <ListBody>
                {filteredList.length === 0 ? (
                  <EmptyText>{t('템플릿이 없습니다')}</EmptyText>
                ) : (
                  filteredList.map(item => (
                    <TemplateListItem
                      key={item.id}
                      isSelected={selectedTemplateId === item.id}
                      onClick={() => setSelectedTemplateId(item.id)}>
                      <TemplateName>{item.templateName}</TemplateName>
                      <TemplatePreview>{item.content}</TemplatePreview>
                    </TemplateListItem>
                  ))
                )}
              </ListBody>
            </ListSection>

            <DetailSection>
              <DetailHeader>
                <DetailTitle>{t('템플릿 내용')}</DetailTitle>
              </DetailHeader>
              <DetailBody>
                {templateDetail?.content ? (
                  <DetailContentText>{templateDetail.content}</DetailContentText>
                ) : (
                  <DetailPlaceholder>
                    {selectedTemplateId ? t('불러오는 중...') : t('왼쪽에서 템플릿을 선택해주세요')}
                  </DetailPlaceholder>
                )}
              </DetailBody>
            </DetailSection>
          </ContentRow>

          <ButtonRow>
            <CancelButton variant="contained" onClick={onClose}>
              {t('취소')}
            </CancelButton>
            <AddButton variant="contained" onClick={handleAdd} disabled={!templateDetail?.content}>
              {t('추가')}
            </AddButton>
          </ButtonRow>
        </ModalContentArea>
      </ModalContainer>
    </Modal>
  );
};

export default SmsTemplateSelectModal;
